/* @flow */
import invariant from 'invariant';
import ChatMessage from 'entities/ChatMessage';
import CurrentUser from 'auth/CurrentUser';
import cx from 'classnames';
import EventTracking from 'lib/EventTracking';
import Link from 'components/Link';
import Message from 'entities/Message';
import MessageItemAttachment from './MessageItemAttachment';
import PropTypes from 'prop-types';
import * as React from 'react';
import TextEntities from 'lib/TextEntities';
import TextWithEntities from 'components/TextWithEntities';
import TranslatableText from 'chat/components/TranslatableText';
import MessageItemLinkPreview from 'chat/components/MessageItemLinkPreview';

import './styles/MessageItemContent.scss';

const UI_CONTEXT = 'chat_message';

type Props = {
  sentBySelf: boolean,
  message: ChatMessage | Message,
  failed: boolean,
  className?: ?string,
};

type State = {
  language: ?string,
};

class MessageItemContent extends React.Component<Props, State> {
  static contextTypes = {
    eventScreenName: PropTypes.string,
  };

  static defaultProps = {
    sentBySelf: false,
    failed: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      language: this.props.message.getISO639Locale(),
    };
  }

  /* Translate the message.
   * Toogles the language between the message locale & the user locale.
   */
  translate = (): void => {
    const shouldTranslateToMessageLocal =
      this.state.language === this.props.message.getISO639Locale();

    const language = shouldTranslateToMessageLocal
      ? CurrentUser.getUser().getISO639Locale()
      : this.props.message.getISO639Locale();

    this.setState({ language });

    const elementName = shouldTranslateToMessageLocal
      ? 'translation_show_translation'
      : 'translation_show_orignal';

    EventTracking.tap(this.context.eventScreenName, elementName, UI_CONTEXT);
  };

  isMessageTranslatable(): boolean {
    const sender = this.props.message.getSender();
    invariant(sender, 'missing sender in isMessageTranslatable');
    return !!// a user is signed in
    (
      CurrentUser.isSignedIn() &&
      // current user was not the sender
      sender.getUuid() !== CurrentUser.getUserUuid() &&
      // the locales are defined on the user & the message
      CurrentUser.getUser().getISO639Locale() &&
      this.props.message.getISO639Locale() &&
      // the locales differs from one another.
      this.props.message.getISO639Locale() !== CurrentUser.getUser().getISO639Locale()
    );
  }

  renderMessageAttachment() {
    const { message } = this.props;
    const file = message.getFile();
    if (!file) {
      return null;
    }
    return <MessageItemAttachment file={file.toGraphqlType()} />;
  }

  renderMessageBody() {
    const { message } = this.props;
    const body = message.getBody();
    if (!body) {
      return null;
    }

    let messageBody = this.isMessageTranslatable() ? (
      <TranslatableText
        initialText={body}
        language={this.state.language}
        bodyAttributes={message.getBodyAttributes()}
      />
    ) : (
      <TextWithEntities
        text={body}
        entities={[].concat(
          TextEntities.parseEmoji(body),
          TextEntities.parseShortcodeEmoji(body),
          TextEntities.parseNewlines(body),
          TextEntities.parseLeadingWhitespace(body),
          TextEntities.parseUrls(body, message.getBodyAttributes())
        )}
        shallow
      />
    );

    if (this.isMessageTranslatable()) {
      // BUG messageBody may have a URL transformed to a Link,
      // which causes a validateDOMNesting <a> error
      messageBody = (
        <Link
          color="inherit"
          onClick={this.translate}
          className="MessageItem-Translatable"
          eventElementName="MessageItem-Translatable">
          {messageBody}
        </Link>
      );
    }

    const hasAttachment = !!message.getFile();

    return (
      <div
        className={cx('MessageItemContent-body', {
          'MessageItemContent-failedBody': this.props.failed,
          'MessageItemContent-bottom': !hasAttachment,
        })}>
        {messageBody}
      </div>
    );
  }

  renderLinkPreviews() {
    const { message } = this.props;
    const linkPreviews = message.getLinkPreviews();

    if (!Array.isArray(linkPreviews) || linkPreviews.length <= 0) {
      return null;
    }

    return (
      <div className="MessageItemBody-linkPreviews">
        {linkPreviews.map(linkPreview => (
          <MessageItemLinkPreview
            key={linkPreview.getUrl()}
            linkPreview={linkPreview.toGraphQL()}
          />
        ))}
      </div>
    );
  }

  render() {
    return (
      <div
        className={cx(this.props.className, {
          'MessageItemContent-self': this.props.sentBySelf,
        })}>
        {this.renderMessageBody()}
        {this.renderMessageAttachment()}
        {this.renderLinkPreviews()}
      </div>
    );
  }
}

export default MessageItemContent;
